import { applyAssetFilter } from './apply-asset-filter.strategy';
import { applyChangedFilter } from './apply-changed-filter.strategy';
import { applyLabelFilter } from './apply-label-filter.strategy';
import { applyRoiIdInFilter } from './apply-roi-id-in-filter.strategy';
import { applyScoreFilter } from './apply-score-filter.strategy';
import { applyUnlabeledFilter } from './apply-unlabeled-filter.strategy';
import type { Roi } from '../../entities';
import type { RoiFilterStrategy, RoiFilteringPipelineInput } from './roi-filter.types';

/**
 * Domain service: chained ROI filters for mosaic panels (strategy registry + pipeline).
 */
export class RoiFilteringService {
  private static readonly strategies: Record<string, RoiFilterStrategy> = {
    label: applyLabelFilter,
    changed: applyChangedFilter,
    score: applyScoreFilter,
    unlabeled: applyUnlabeledFilter,
    roiId: applyRoiIdInFilter,
    asset: applyAssetFilter,
  };

  /**
   * Runs filter steps in order; each step narrows the ROI id set under `panel`.
   * When `filters` is empty, returns `galleryLabelIndex` unchanged.
   */
  public static applyFilters(input: RoiFilteringPipelineInput): Map<string, Set<string>> {
    const { filters, rois, selectedCropIds, changedRois, labelIndex, galleryLabelIndex, labels, assetIndex } = input;

    const filteredLabelIndex = filters.length > 0 ? new Map<string, Set<string>>() : galleryLabelIndex;
    const filterKey = `panel`;

    for (const step of filters) {
      const previousFilterRoiIds = filteredLabelIndex.get(filterKey) ?? new Set(Object.keys(rois));
      const previousFilterRois: Roi[] = [];

      for (const roiId of previousFilterRoiIds) {
        const roi = rois[roiId];
        if (roi === undefined) continue;
        previousFilterRois.push(roi);
      }

      const strategy = this.strategies[step.fieldId];

      if (!strategy) continue;

      const next = strategy(step, {
        previousRoiIds: previousFilterRoiIds,
        previousRois: previousFilterRois,
        rois,
        changedRois,
        labelIndex,
        selectedCropIds,
        assetIndex,
        labels,
      });

      if (next === undefined) continue;

      filteredLabelIndex.set(filterKey, next);
    }

    return filteredLabelIndex;
  }
}
